import { computed } from 'vue'

import type { Component, ComputedRef } from 'vue'
import type { ScopeGateArea, ScopeSkeleton } from '@ts-scope/types'
import type { ScopeStatus } from '@utils/stores/scoped-entry'

import { useScopeReadiness } from '@composables/widgets/scope/scope-readiness.hook'

import SkeletonSectionDemo from '@components/Skeletons/Demo/SkeletonSectionDemo.vue'
import SkeletonAllLessons from '@components/Skeletons/Paid/AllLessons/SkeletonAllLessons.vue'
import SkeletonProgressReports from '@components/Skeletons/ProgressReports/SkeletonProgressReports.vue'
import SkeletonLearningProgress from '@components/Skeletons/LearningProgress/SkeletonLearningProgress.vue'
import SkeletonChangeWeeklySchedule from '@components/Skeletons/Paid/DisciplineSchedule/SkeletonChangeWeeklySchedule.vue'
import SkeletonFilesSection from '@components/Skeletons/Files/SkeletonFilesSection.vue'
import { LoaderBlockDomain } from '@UI/Loaders/LoaderBlockDomain'

interface GateScopeOptions {
  area: ScopeGateArea
}

interface GateScopeReturn {
  status: ComputedRef<ScopeStatus>
  isSkeletonShown: ComputedRef<boolean>
  skeletonComponent: ComputedRef<Component>
  retry: () => void
}

const SKELETON_COMPONENTS: Record<ScopeSkeleton, Component> = {
  demo: SkeletonSectionDemo,
  lessons: SkeletonAllLessons,
  progressReports: SkeletonProgressReports,
  learningProgress: SkeletonLearningProgress,
  schedule: SkeletonChangeWeeklySchedule,
  files: SkeletonFilesSection,
  block: LoaderBlockDomain,
}

const isSkeletonStatus = (status: ScopeStatus): boolean => {
  switch (status) {
    case 'loading':
      return true
    case 'error':
      // TODO(INFRA-724): error block with retry
      return false
    case 'idle':
    case 'ready':
      return false
  }
}

export const useGateScope = ({ area }: GateScopeOptions): GateScopeReturn => {
  const { status, skeleton, gate, retry } = useScopeReadiness()

  const isGatedHere = computed<boolean>(() => gate.value === area)

  const isSkeletonShown = computed<boolean>(() => isGatedHere.value && isSkeletonStatus(status.value))

  const skeletonComponent = computed<Component>(() => SKELETON_COMPONENTS[skeleton.value])

  return {
    status,
    isSkeletonShown,
    skeletonComponent,
    retry,
  }
}
